import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  listPixels, getPixelMeta, listPixelDataBodies, createPixel, updatePixel, setPixelStatus, rotatePixelKey,
  getPixelReport, testPixelDelivery, MASK,
  PixelRow, PixelInput, PixelDestination, PixelStats, PixelReport, DataBodyOption, CustomAuth,
} from '../../services/pixels';
import { BigSkyEnrichmentShell } from '../Vertebrae/BigSkyEnrichmentShell';
import { OWNED_ATTRIBUTE_CATALOG } from '../Vertebrae/ownedAttributeCatalog';

// ---------------------------------------------------------------------------
// Identity Pixels: super_admin board. Every pixel's status, volume, matches
// and delivery health, plus create/edit, install snippet and per-pixel report.
// ---------------------------------------------------------------------------

const th: React.CSSProperties = {
  textAlign: 'left', padding: '10px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4,
  color: '#e5e7eb', borderBottom: '1px solid #2d3142', whiteSpace: 'nowrap',
  background: '#1a1d27',
};
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13, color: '#1f2937', background: '#ffffff', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' };
const num: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const label: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: '#4b5563', marginBottom: 4 };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, color: '#1f2937', background: '#ffffff', border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' };
const area: React.CSSProperties = { ...input, minHeight: 70, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 };
const field: React.CSSProperties = { marginBottom: 14 };
const sectionHead: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, margin: '18px 0 10px', borderTop: '1px solid #e5e7eb', paddingTop: 14 };
const hint: React.CSSProperties = { fontSize: 11, color: '#6b7280', marginTop: 4 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px', zIndex: 1000 };
const modal = (w: number): React.CSSProperties => ({ width: '100%', maxWidth: w, background: '#ffffff', color: '#1f2937', border: '1px solid #e5e7eb', borderRadius: 10, padding: 24, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' });
const pre: React.CSSProperties = { background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, fontSize: 12, color: '#1f2937', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 };

const fmt = (n: number | null | undefined) => (n == null ? '—' : Number(n).toLocaleString());
function ago(iso: string | null | undefined): string {
  if (!iso) { return 'never'; }
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 90) { return `${Math.max(1, Math.round(s))}s ago`; }
  if (s < 5400) { return `${Math.round(s / 60)}m ago`; }
  if (s < 172800) { return `${Math.round(s / 3600)}h ago`; }
  return `${Math.round(s / 86400)}d ago`;
}

function Badge({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, color, background: `${color}22`, border: `1px solid ${color}55`, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

// ---- text <-> structure helpers for the editor ----
const lines = (s: string) => s.split('\n').map(l => l.trim()).filter(Boolean);
function kvToText(o: Record<string, string>, sep: string) { return Object.entries(o).map(([k, v]) => `${k}${sep}${v}`).join('\n'); }
function textToKv(s: string, sep: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const l of lines(s)) {
    const i = l.indexOf(sep);
    if (i > 0) { out[l.slice(0, i).trim()] = l.slice(i + sep.length).trim(); }
  }
  return out;
}

const SUM = (s: PixelStats | undefined, k: keyof PixelStats) => (s ? s[k] : 0);
const deterministic = (s?: PixelStats) => SUM(s, 'matches_email') + SUM(s, 'matches_phone') + SUM(s, 'matches_md5') + SUM(s, 'matches_visitor');

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

type PassMode = 'core' | 'extended' | 'all' | 'custom';
interface MapRow { id: string; to: string; from: string; required: boolean }
interface StaticRow { id: string; key: string; value: string }

interface Draft {
  name: string; env: 'test' | 'live'; domains: string; staticAttrs: string; allowlist: string;
  formCapture: boolean; ipMatching: boolean; dedupeDays: string; minConfidence: string; sensitive: string; notes: string;
  passMode: PassMode; passAttributes: string[];
  destType: 'none' | 'data_body' | 'custom_api'; dataBodyId: string; dataBodyName: string;
  url: string; method: 'POST' | 'PUT' | 'PATCH'; authType: CustomAuth['type']; authA: string; authB: string;
  headersRaw: string; mappings: MapRow[]; statics: StaticRow[]; timeoutMs: string;
}

const DEFAULT_SENSITIVE = ['/health', '/medical', '/pharmacy', '/patient', '/kids', '/children', '/bankruptcy', '/debt-relief'];

const ALL_ATTR_KEYS = OWNED_ATTRIBUTE_CATALOG.flatMap(g => g.attributes.map(a => a.key));
const ATTR_LABEL = new Map(OWNED_ATTRIBUTE_CATALOG.flatMap(g => g.attributes.map(a => [a.key, a.label] as [string, string])));
const EXTENDED_ATTRS = [
  'first_name', 'last_name', 'personal_email', 'business_email', 'mobile_phone', 'personal_phones', 'phone_type',
  'mobile_carrier', 'mobile_dnc', 'personal_address', 'personal_address_2', 'personal_city', 'personal_state',
  'personal_zip', 'personal_zip4', 'gender', 'age_range', 'income_range', 'net_worth', 'homeowner', 'married',
  'children', 'job_title', 'seniority_level', 'department', 'company_name', 'company_domain', 'company_revenue',
  'company_employee_count', 'linkedin_url',
].filter(k => ALL_ATTR_KEYS.includes(k));
/** The default set, by its friendly output names. */
const CORE_LABELS = 'email, phone, first/last name, address, city, state, zip, gender, age range, income range, net worth, homeowner, married, children, job title, company, LinkedIn';
/** Visit fields every lead carries (Data Bodies counts these toward its 50-field limit too). */
const META_COUNT = 20;

let rowSeq = 0;
const rid = () => `r${++rowSeq}`;

function modeOf(list: string[] | null): PassMode {
  if (!list) { return 'core'; }
  const set = new Set(list);
  if (list.length === ALL_ATTR_KEYS.length && ALL_ATTR_KEYS.every(k => set.has(k))) { return 'all'; }
  if (list.length === EXTENDED_ATTRS.length && EXTENDED_ATTRS.every(k => set.has(k))) { return 'extended'; }
  return 'custom';
}

function draftFrom(p: PixelRow | null, env: 'test' | 'live'): Draft {
  const d = p?.destination ?? { type: 'none' as const };
  const c = d.type === 'custom_api' ? d : null;
  const a: CustomAuth = c?.auth ?? { type: 'none' };
  const pass = p?.passAttributes ?? null;
  return {
    name: p?.name ?? '', env: p?.env ?? env,
    domains: (p?.allowedDomains ?? []).join('\n'),
    staticAttrs: kvToText(p?.staticAttributes ?? {}, '='),
    allowlist: (p?.attributeAllowlist ?? []).join(', '),
    formCapture: p?.formCapture ?? false, ipMatching: p?.ipMatching ?? false,
    dedupeDays: String(p?.dedupeDays ?? 30), minConfidence: String(p?.minConfidence ?? 0),
    sensitive: (p?.sensitivePatterns ?? DEFAULT_SENSITIVE).join('\n'), notes: p?.notes ?? '',
    passMode: modeOf(pass), passAttributes: pass ?? [],
    destType: d.type, dataBodyId: d.type === 'data_body' ? (d.dataBodyId ?? '') : '', dataBodyName: d.type === 'data_body' ? (d.dataBodyName ?? '') : '',
    url: c?.url ?? '', method: c?.method ?? 'POST', authType: a.type,
    authA: a.type === 'bearer' ? a.token : a.type === 'basic' ? a.username : a.type === 'header' ? a.name : '',
    authB: a.type === 'basic' ? a.password : a.type === 'header' ? a.value : '',
    headersRaw: c && Object.keys(c.headers || {}).length ? JSON.stringify(c.headers, null, 2) : '',
    mappings: (c?.fieldMap ?? []).map(f => ({ id: rid(), to: f.to, from: f.from, required: Boolean(f.required) })),
    statics: Object.entries(c?.staticBody ?? {}).map(([key, value]) => ({ id: rid(), key, value })),
    timeoutMs: c?.timeoutMs ? String(c.timeoutMs) : '',
  };
}

function parseHeaders(raw: string): Record<string, string> | null {
  if (!raw.trim()) { return {}; }
  try {
    const v = JSON.parse(raw);
    if (!v || typeof v !== 'object' || Array.isArray(v)) { return null; }
    const out: Record<string, string> = {};
    for (const [k, val] of Object.entries(v)) { out[k] = String(val); }
    return out;
  } catch { return null; }
}

function inputFrom(d: Draft, existing: PixelRow | null): PixelInput {
  let destination: PixelDestination = { type: 'none' };
  if (d.destType === 'data_body') {
    const unchanged = existing?.destination.type === 'data_body' && existing.destination.dataBodyId === d.dataBodyId;
    destination = unchanged ? { type: 'data_body', webhookUrl: MASK } : { type: 'data_body', dataBodyId: d.dataBodyId };
  } else if (d.destType === 'custom_api') {
    let a: CustomAuth = { type: 'none' };
    if (d.authType === 'bearer') { a = { type: 'bearer', token: d.authA }; }
    if (d.authType === 'basic') { a = { type: 'basic', username: d.authA, password: d.authB }; }
    if (d.authType === 'header') { a = { type: 'header', name: d.authA, value: d.authB }; }
    const staticBody: Record<string, string> = {};
    for (const s of d.statics) { if (s.key.trim()) { staticBody[s.key.trim()] = s.value; } }
    destination = {
      type: 'custom_api', url: d.url.trim(), method: d.method, auth: a,
      headers: parseHeaders(d.headersRaw) ?? {},
      fieldMap: d.mappings.filter(m => m.to.trim() && m.from).map(m => ({ from: m.from, to: m.to.trim(), required: m.required })),
      staticBody,
      ...(d.timeoutMs ? { timeoutMs: parseInt(d.timeoutMs, 10) } : {}),
    };
  }
  return {
    name: d.name.trim(), env: d.env,
    allowedDomains: lines(d.domains),
    staticAttributes: textToKv(d.staticAttrs, '='),
    attributeAllowlist: d.allowlist.split(',').map(s => s.trim()).filter(Boolean),
    formCapture: d.formCapture, ipMatching: d.ipMatching,
    dedupeDays: parseInt(d.dedupeDays, 10) || 0, minConfidence: parseInt(d.minConfidence, 10) || 0,
    sensitivePatterns: lines(d.sensitive),
    passAttributes: d.passMode === 'core' ? null : d.passAttributes,
    destination, notes: d.notes.trim() || null,
  };
}

const presetBtn = (on: boolean): React.CSSProperties => ({
  padding: '8px 12px', borderRadius: 6, fontSize: 13, cursor: 'pointer', textAlign: 'left', flex: 1, minWidth: 130,
  border: `1px solid ${on ? '#6366f1' : '#d1d5db'}`, background: on ? '#eef2ff' : '#ffffff', color: '#1f2937',
});
const mapBox: React.CSSProperties = { background: '#f8f9fb', border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 };
const iconBtn: React.CSSProperties = { width: 28, height: 28, border: '1px solid #e5e7eb', borderRadius: 6, background: '#ffffff', cursor: 'pointer', fontSize: 12, fontWeight: 700 };

function Editor({ pixel, env, bodies, coreFields, metaFields, onClose, onSaved }: {
  pixel: PixelRow | null; env: 'test' | 'live'; bodies: DataBodyOption[]; coreFields: string[]; metaFields: string[];
  onClose: () => void; onSaved: (snippet?: string) => void;
}) {
  const { getToken } = useAuth();
  const [d, setD] = useState<Draft>(() => draftFrom(pixel, env));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [testMsg, setTestMsg] = useState('');
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD(prev => ({ ...prev, [k]: v }));

  const choosePreset = (m: PassMode) => {
    if (m === 'core') { setD(p => ({ ...p, passMode: 'core', passAttributes: [] })); }
    if (m === 'extended') { setD(p => ({ ...p, passMode: 'extended', passAttributes: [...EXTENDED_ATTRS] })); }
    if (m === 'all') { setD(p => ({ ...p, passMode: 'all', passAttributes: [...ALL_ATTR_KEYS] })); }
    if (m === 'custom') { setD(p => ({ ...p, passMode: 'custom', passAttributes: p.passMode === 'core' ? [...EXTENDED_ATTRS] : p.passAttributes })); }
  };

  // Fields a mapping row can pick from: what this pixel will actually send.
  const fieldOptions = useMemo(() => {
    const attrs = d.passMode === 'core' ? coreFields : ['email', 'phone', ...d.passAttributes];
    const extra = [...Object.keys(textToKv(d.staticAttrs, '=')), ...d.allowlist.split(',').map(s => s.trim()).filter(Boolean)];
    return { attrs, meta: metaFields, extra };
  }, [d.passMode, d.passAttributes, d.staticAttrs, d.allowlist, coreFields, metaFields]);

  const attrCount = d.passMode === 'core' ? coreFields.length : d.passAttributes.length + 2;
  const overBodyLimit = d.destType === 'data_body' && attrCount + META_COUNT > 50;
  const headersBad = d.destType === 'custom_api' && parseHeaders(d.headersRaw) === null;

  const updMap = (id: string, patch: Partial<MapRow>) => set('mappings', d.mappings.map(m => (m.id === id ? { ...m, ...patch } : m)));
  const updStatic = (id: string, patch: Partial<StaticRow>) => set('statics', d.statics.map(s => (s.id === id ? { ...s, ...patch } : s)));

  const save = async () => {
    if (headersBad) { setErr('Custom headers must be a JSON object.'); return; }
    setSaving(true); setErr('');
    try {
      const token = await getToken();
      if (!token) { setErr('Session expired'); return; }
      const body = inputFrom(d, pixel);
      if (pixel) {
        const { env: _e, ...patch } = body;
        await updatePixel(token, pixel.id, patch);
        onSaved();
      } else {
        const r = await createPixel(token, body);
        onSaved(r.snippet);
      }
    } catch (e: any) {
      const issues = e?.response?.data?.issues;
      setErr(e?.response?.data?.error + (issues ? `: ${issues.map((i: any) => `${i.path.join('.')} ${i.message}`).join('; ')}` : '') || e.message);
    } finally { setSaving(false); }
  };

  const test = async () => {
    if (!pixel) { return; }
    setTestMsg('Sending test lead…');
    try {
      const token = await getToken();
      if (!token) { return; }
      const r = await testPixelDelivery(token, pixel.id);
      setTestMsg(r.result.ok ? `Delivered (HTTP ${r.result.status}, ${r.result.durationMs} ms)` : `Failed: ${r.result.error ?? 'unknown'}${r.result.response ? ` | ${r.result.response.slice(0, 200)}` : ''}`);
    } catch (e: any) { setTestMsg(e?.response?.data?.error || e.message); }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal(820)} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 16px', fontSize: 18 }}>{pixel ? `Edit pixel: ${pixel.name}` : 'New pixel'}</h2>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <div style={field}><label style={label}>Name</label><input style={input} value={d.name} onChange={e => set('name', e.target.value)} placeholder="Acme, homepage" /></div>
          <div style={field}><label style={label}>Environment</label>
            <input style={{ ...input, opacity: 0.7 }} value={d.env === 'test' ? 'TEST (staging)' : 'LIVE (production)'} disabled />
          </div>
        </div>
        <div style={field}><label style={label}>Allowed domains (one per line, subdomains included)</label>
          <textarea style={area} value={d.domains} onChange={e => set('domains', e.target.value)} placeholder={'acme.com\nshop-acme.com'} />
          <div style={hint}>Hits from any other site are dropped. Required before activating.</div>
        </div>

        {/* ---------- What to send: same picker as the Identity Enrichment vertebra ---------- */}
        <div style={sectionHead}>Identity attributes to pass</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          {([
            ['core', 'Core (default)', 'Contact, address + key demographics'],
            ['extended', 'Extended', `${EXTENDED_ATTRS.length} fields: + phone type/carrier/DNC, business, company`],
            ['all', 'All', `Every graph attribute (${ALL_ATTR_KEYS.length})`],
            ['custom', 'Custom', 'Pick individual attributes'],
          ] as Array<[PassMode, string, string]>).map(([m, t, sub]) => (
            <button key={m} type="button" style={presetBtn(d.passMode === m)} onClick={() => choosePreset(m)}>
              <div style={{ fontWeight: 600 }}>{t}</div><div style={{ fontSize: 11, color: '#6b7280' }}>{sub}</div>
            </button>
          ))}
        </div>
        {d.passMode === 'core' ? (
          <div style={{ ...hint, fontSize: 12, marginBottom: 10 }}>Sends: {CORE_LABELS}, plus the visit fields (page, UTMs, match type, confidence).</div>
        ) : (
          <BigSkyEnrichmentShell
            selected={d.passAttributes}
            onChange={next => setD(p => ({ ...p, passAttributes: next, passMode: modeOf(next) === 'all' ? 'all' : modeOf(next) === 'extended' ? 'extended' : 'custom' }))}
            catalog={OWNED_ATTRIBUTE_CATALOG}
            showProvisionalNote={false}
            showBillingNote={false}
          />
        )}
        {d.passMode !== 'core' && <div style={hint}>Every lead also carries a best-pick <code>email</code> and <code>phone</code>. Attributes empty for a person are left out.</div>}
        {overBodyLimit && (
          <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#78350f', marginTop: 8 }}>
            About {attrCount + META_COUNT} fields per lead. A Data Body tracks up to 50 fields, and new field names build indexes on first arrival. Keep Data Bodies destinations to Core or Extended; use a Custom API for larger sets.
          </div>
        )}

        <div style={sectionHead}>Destination (real time)</div>
        <div style={field}>
          <select style={input} value={d.destType} onChange={e => set('destType', e.target.value as Draft['destType'])}>
            <option value="none">None (collect + match only)</option>
            <option value="data_body">Data Bodies body</option>
            <option value="custom_api">Custom API (JSON)</option>
          </select>
        </div>
        {d.destType === 'data_body' && (
          <div style={field}><label style={label}>Data body</label>
            <select style={input} value={d.dataBodyId} onChange={e => set('dataBodyId', e.target.value)}>
              <option value="">Select…</option>
              {bodies.map(b => <option key={b.id} value={b.id}>{b.org_name ? `${b.org_name} / ` : ''}{b.name}{b.status !== 'active' ? ` (${b.status})` : ''}</option>)}
            </select>
            <div style={hint}>Leads arrive through the body's webhook and run through its vertebrae and legs.</div>
          </div>
        )}
        {d.destType === 'custom_api' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 120px', gap: 12 }}>
              <div style={field}><label style={label}>URL (https)</label><input style={input} value={d.url} onChange={e => set('url', e.target.value)} placeholder="https://api.customer.com/leads" /></div>
              <div style={field}><label style={label}>Method</label>
                <select style={input} value={d.method} onChange={e => set('method', e.target.value as Draft['method'])}><option>POST</option><option>PUT</option><option>PATCH</option></select>
              </div>
              <div style={field}><label style={label}>Timeout ms</label><input style={input} value={d.timeoutMs} onChange={e => set('timeoutMs', e.target.value)} placeholder="10000" /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 1fr', gap: 12 }}>
              <div style={field}><label style={label}>Auth</label>
                <select style={input} value={d.authType} onChange={e => set('authType', e.target.value as CustomAuth['type'])}>
                  <option value="none">None</option><option value="bearer">Bearer token</option><option value="basic">Basic</option><option value="header">API key header</option>
                </select>
              </div>
              {d.authType !== 'none' && (
                <div style={field}><label style={label}>{d.authType === 'bearer' ? 'Token' : d.authType === 'basic' ? 'Username' : 'Header name'}</label>
                  <input style={input} value={d.authA} onChange={e => set('authA', e.target.value)} /></div>
              )}
              {(d.authType === 'basic' || d.authType === 'header') && (
                <div style={field}><label style={label}>{d.authType === 'basic' ? 'Password' : 'Header value'}</label>
                  <input style={input} value={d.authB} onChange={e => set('authB', e.target.value)} /></div>
              )}
            </div>
            <div style={field}><label style={label}>Custom headers (JSON)</label>
              <textarea style={area} value={d.headersRaw} onChange={e => set('headersRaw', e.target.value)} placeholder='{"X-Source": "pixel"}' />
              {headersBad ? <div style={{ ...hint, color: '#dc2626' }}>Must be a JSON object.</div>
                : <div style={hint}>Saved secret values show as {MASK}; leave them to keep the stored value.</div>}
            </div>

            {/* Field mapping: same pattern as the Custom API leg */}
            <div style={field}><label style={label}>Field mapping</label>
              <div style={mapBox}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6, display: 'flex' }}>
                  <span style={{ flex: 1 }}>Destination field name</span><span style={{ width: 20 }} /><span style={{ flex: 1 }}>Pixel field</span><span style={{ width: 64, textAlign: 'center' }}>Req.</span>
                </div>
                {d.mappings.length === 0 && <div style={{ fontSize: 12, color: '#9ca3af', padding: '8px 0', textAlign: 'center' }}>No mapping: the entire lead payload is sent as-is.</div>}
                {d.mappings.map(m => (
                  <div key={m.id} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                    <input style={{ ...input, flex: 1 }} placeholder="contact.email" value={m.to} onChange={e => updMap(m.id, { to: e.target.value })} />
                    <span style={{ color: '#9ca3af' }}>&rarr;</span>
                    <select style={{ ...input, flex: 1 }} value={m.from} onChange={e => updMap(m.id, { from: e.target.value })}>
                      <option value="">— select a pixel field —</option>
                      <optgroup label="Identity">{fieldOptions.attrs.map(k => <option key={k} value={k}>{ATTR_LABEL.get(k) ?? k}</option>)}</optgroup>
                      <optgroup label="Visit">{fieldOptions.meta.map(k => <option key={k} value={k}>{k}</option>)}</optgroup>
                      {fieldOptions.extra.length > 0 && <optgroup label="Your attributes">{fieldOptions.extra.map(k => <option key={k} value={k}>{k}</option>)}</optgroup>}
                    </select>
                    <button type="button" style={{ ...iconBtn, color: m.required ? '#4f46e5' : '#9ca3af', background: m.required ? '#eef2ff' : '#ffffff' }}
                      title={m.required ? 'Required: the lead is NOT sent if this field is empty. Click to make optional.' : 'Optional: omitted if empty, lead still sends. Click to make required.'}
                      onClick={() => updMap(m.id, { required: !m.required })}>{m.required ? 'R' : 'O'}</button>
                    <button type="button" style={{ ...iconBtn, color: '#ef4444' }} title="Remove" onClick={() => set('mappings', d.mappings.filter(x => x.id !== m.id))}>x</button>
                  </div>
                ))}
                <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 6 }}
                  onClick={() => set('mappings', [...d.mappings, { id: rid(), to: '', from: '', required: false }])}>+ Add Mapping</button>
                <div style={hint}>Dots nest: <code>contact.email</code> sends {'{ "contact": { "email": … } }'}.</div>
              </div>
            </div>

            <div style={field}><label style={label}>Static values</label>
              <div style={mapBox}>
                {d.statics.length === 0 && <div style={{ fontSize: 12, color: '#9ca3af', padding: '8px 0', textAlign: 'center' }}>No static values configured.</div>}
                {d.statics.map(s => (
                  <div key={s.id} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                    <input style={{ ...input, flex: 1 }} placeholder="list_id" value={s.key} onChange={e => updStatic(s.id, { key: e.target.value })} />
                    <span style={{ color: '#9ca3af' }}>=</span>
                    <input style={{ ...input, flex: 1 }} placeholder="abc-123" value={s.value} onChange={e => updStatic(s.id, { value: e.target.value })} />
                    <button type="button" style={{ ...iconBtn, color: '#ef4444' }} title="Remove" onClick={() => set('statics', d.statics.filter(x => x.id !== s.id))}>x</button>
                  </div>
                ))}
                <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 6 }}
                  onClick={() => set('statics', [...d.statics, { id: rid(), key: '', value: '' }])}>+ Add Static Value</button>
              </div>
            </div>
          </>
        )}

        <div style={sectionHead}>Your attributes passed back</div>
        <div style={field}><label style={label}>Static attributes (key=value per line, added to every lead)</label>
          <textarea style={area} value={d.staticAttrs} onChange={e => set('staticAttrs', e.target.value)} placeholder={'client_id=ACME\ncampaign=fall26'} />
        </div>
        <div style={field}><label style={label}>Page attribute allowlist (comma separated; empty = any key)</label>
          <input style={input} value={d.allowlist} onChange={e => set('allowlist', e.target.value)} placeholder="order_id, tag, sub_id" />
          <div style={hint}>Sites pass these with trq.push(['set', {'{'} order_id: '123' {'}'}]). Max 20 keys, 256 chars each.</div>
        </div>

        <div style={sectionHead}>Matching</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ ...field, display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
            <input type="checkbox" checked={d.formCapture} onChange={e => set('formCapture', e.target.checked)} />
            <span>Form capture<div style={hint}>Reads email/phone fields only, on change/submit, hashed in the browser.</div></span>
          </label>
          <label style={{ ...field, display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
            <input type="checkbox" checked={d.ipMatching} onChange={e => set('ipMatching', e.target.checked)} />
            <span>IP-only matching<div style={hint}>Probabilistic, confidence 40. Off by default.</div></span>
          </label>
          <div style={field}><label style={label}>Dedupe window (days, 0 = off)</label><input style={input} type="number" min={0} max={365} value={d.dedupeDays} onChange={e => set('dedupeDays', e.target.value)} /></div>
          <div style={field}><label style={label}>Min confidence (0-100)</label><input style={input} type="number" min={0} max={100} value={d.minConfidence} onChange={e => set('minConfidence', e.target.value)} /></div>
        </div>
        <div style={field}><label style={label}>Sensitive URL patterns (never collected; one per line)</label>
          <textarea style={area} value={d.sensitive} onChange={e => set('sensitive', e.target.value)} />
        </div>
        <div style={field}><label style={label}>Notes</label><textarea style={area} value={d.notes} onChange={e => set('notes', e.target.value)} /></div>

        {err && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{err}</div>}
        {testMsg && <div style={{ color: '#374151', fontSize: 13, marginBottom: 12 }}>{testMsg}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {pixel && pixel.destination.type !== 'none' && <button className="btn btn-secondary" onClick={() => void test()}>Send test lead</button>}
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={saving || !d.name.trim()} onClick={() => void save()}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Install + report
// ---------------------------------------------------------------------------

function Install({ pixel, snippet, onClose, onRotated }: { pixel: PixelRow; snippet: string; onClose: () => void; onRotated: () => void }) {
  const { getToken } = useAuth();
  const [snip, setSnip] = useState(snippet);
  const [copied, setCopied] = useState(false);
  const copy = () => { void navigator.clipboard.writeText(snip).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); };
  const rotate = async () => {
    if (!window.confirm('Issue a new key? The current snippet stops working immediately.')) { return; }
    const token = await getToken();
    if (!token) { return; }
    const r = await rotatePixelKey(token, pixel.id);
    setSnip(r.snippet);
    onRotated();
  };
  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal(720)} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 16px', fontSize: 18 }}>Install: {pixel.name}</h2>
        <label style={label}>1. Paste before &lt;/head&gt; on every page</label>
        <pre style={pre}>{snip}</pre>
        <div style={{ margin: '8px 0 18px', display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={copy}>{copied ? 'Copied' : 'Copy snippet'}</button>
          <button className="btn btn-secondary" onClick={() => void rotate()}>Rotate key</button>
        </div>
        <label style={label}>2. Optional: pass attributes / known identity</label>
        <pre style={pre}>{`trq.push(['set', { order_id: '123', tag: 'vip' }]);      // returned on the lead
trq.push(['identify', { email: 'user@site.com' }]);        // on login / checkout / lead form
trq.push(['identify', { email_sha256: '<hex>' }]);         // if you only have the hash
trq.push(['track', 'signup']);                             // custom event`}</pre>
        <label style={{ ...label, marginTop: 18 }}>3. Optional: email links</label>
        <pre style={pre}>{'https://site.com/offer?_tre={{sha256 of lowercased email}}'}</pre>
        <div style={hint}>The param is read, sent hashed and stripped from the address bar.</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

function Report({ pixelId, onClose }: { pixelId: string; onClose: () => void }) {
  const { getToken } = useAuth();
  const [r, setR] = useState<PixelReport | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    void (async () => {
      try {
        const token = await getToken();
        if (!token) { return; }
        setR(await getPixelReport(token, pixelId, 30));
      } catch (e: any) { setErr(e?.response?.data?.error || e.message); }
    })();
  }, [getToken, pixelId]);

  const small: React.CSSProperties = { ...td, fontSize: 12, padding: '6px 8px' };
  const smallNum: React.CSSProperties = { ...small, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const smallTh: React.CSSProperties = { ...th, padding: '6px 8px' };
  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal(1100)} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 16px', fontSize: 18 }}>Report: {r?.pixel.name ?? '…'}</h2>
        {err && <div style={{ color: '#ef4444' }}>{err}</div>}
        {!r && !err && <div style={{ color: '#9ca3af' }}>Loading…</div>}
        {r && (
          <>
            <div style={sectionHead}>Daily (UTC, last 30 days)</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>{['Day', 'Pings', 'Visitors', 'Bots', 'GPC', 'Off-domain', 'Lookups', 'Email', 'Phone', 'Visitor', 'IP', 'No match', 'Deduped', 'Delivered', 'Failed'].map(h => <th key={h} style={smallTh}>{h}</th>)}</tr></thead>
                <tbody>
                  {[...r.daily].reverse().map(d => (
                    <tr key={String(d.day)}>
                      <td style={small}>{String(d.day).slice(0, 10)}</td>
                      {['pings', 'unique_visitors', 'bots', 'gpc_optouts', 'rejected_domain', 'lookups', 'matches_email', 'matches_phone', 'matches_visitor', 'matches_ip', 'no_match', 'dup_suppressed', 'delivered', 'delivery_failed']
                        .map(k => <td key={k} style={smallNum}>{fmt(Number(d[k]))}</td>)}
                    </tr>
                  ))}
                  {!r.daily.length && <tr><td style={small} colSpan={15}>No traffic yet.</td></tr>}
                </tbody>
              </table>
            </div>

            <div style={sectionHead}>Recent matches</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>{['When', 'Type', 'Key', 'Conf.', 'Page', 'Delivery', 'Tries', 'Last error'].map(h => <th key={h} style={smallTh}>{h}</th>)}</tr></thead>
                <tbody>
                  {r.recentMatches.map(m => (
                    <tr key={m.id}>
                      <td style={small}>{ago(m.matched_at)}</td><td style={small}>{m.match_type}</td><td style={small}>{m.matched_key_type}</td>
                      <td style={smallNum}>{m.confidence}</td>
                      <td style={{ ...small, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.page_url}>{m.page_url}</td>
                      <td style={small}><Badge color={m.delivery_status === 'delivered' ? '#22c55e' : m.delivery_status === 'failed' ? '#ef4444' : '#9ca3af'}>{m.delivery_status}</Badge></td>
                      <td style={smallNum}>{m.delivery_attempts}</td><td style={small}>{m.last_error ?? ''}</td>
                    </tr>
                  ))}
                  {!r.recentMatches.length && <tr><td style={small} colSpan={8}>No matches yet.</td></tr>}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div>
                <div style={sectionHead}>Top pages (24h)</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
                  {r.topPages.map(p => <tr key={p.page}><td style={{ ...small, wordBreak: 'break-all' }}>{p.page}</td><td style={smallNum}>{fmt(p.hits)}</td></tr>)}
                  {!r.topPages.length && <tr><td style={small}>None.</td></tr>}
                </tbody></table>
              </div>
              <div>
                <div style={sectionHead}>Delivery attempts</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
                  {r.recentAttempts.map((a, i) => (
                    <tr key={i}><td style={small}>{ago(a.attempted_at)}</td><td style={small}><Badge color={a.ok ? '#22c55e' : '#ef4444'}>{a.status_code ?? 'ERR'}</Badge></td>
                      <td style={smallNum}>{a.duration_ms} ms</td><td style={{ ...small, wordBreak: 'break-all' }}>{a.error ?? ''}</td></tr>
                  ))}
                  {!r.recentAttempts.length && <tr><td style={small}>None.</td></tr>}
                </tbody></table>
              </div>
            </div>

            <div style={sectionHead}>Live events (24h, newest first)</div>
            <div style={{ overflowX: 'auto', maxHeight: 280, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>{['When', 'Visitor', 'Event', 'IP class', 'Has ID', 'URL', 'Attributes'].map(h => <th key={h} style={smallTh}>{h}</th>)}</tr></thead>
                <tbody>
                  {r.recentEvents.map((e, i) => (
                    <tr key={i}>
                      <td style={small}>{ago(e.received_at)}</td><td style={small}>{String(e.visitor_id).slice(0, 8)}</td><td style={small}>{e.event_type}</td>
                      <td style={small}>{e.ip_class}</td><td style={small}>{e.has_ids ? 'yes' : ''}</td>
                      <td style={{ ...small, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.url}>{e.url}</td>
                      <td style={small}>{Object.keys(e.custom_attrs || {}).length ? JSON.stringify(e.custom_attrs) : ''}</td>
                    </tr>
                  ))}
                  {!r.recentEvents.length && <tr><td style={small} colSpan={7}>No events in the last 24h.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Board
// ---------------------------------------------------------------------------

export default function Pixels() {
  const { user, getToken } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [env, setEnv] = useState<'test' | 'live'>('test');
  const [pixels, setPixels] = useState<PixelRow[]>([]);
  const [bodies, setBodies] = useState<DataBodyOption[]>([]);
  const [fields, setFields] = useState<{ core: string[]; meta: string[] }>({ core: [], meta: [] });
  const [origin, setOrigin] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<PixelRow | null | 'new'>(null);
  const [installing, setInstalling] = useState<{ pixel: PixelRow; snippet: string } | null>(null);
  const [reporting, setReporting] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [range, setRange] = useState<'today' | 'd7' | 'd30'>('d7');

  const load = useCallback(async () => {
    setError('');
    try {
      const token = await getToken();
      if (!token) { setError('Your session expired. Please sign in again.'); return; }
      const r = await listPixels(token);
      setEnv(r.env);
      setPixels(r.pixels);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to load pixels');
    } finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => {
    if (!isSuperAdmin) { return; }
    void load();
    const t = setInterval(() => { void load(); }, 30_000);
    return () => clearInterval(t);
  }, [isSuperAdmin, load]);

  useEffect(() => {
    if (!isSuperAdmin) { return; }
    void (async () => {
      const token = await getToken();
      if (!token) { return; }
      try { setBodies(await listPixelDataBodies(token)); } catch { /* picker only */ }
      try { const m = await getPixelMeta(token); setFields({ core: m.coreFields ?? m.payloadFields.slice(0, 17), meta: m.metaFields ?? m.payloadFields.slice(17) }); setOrigin(m.publicOrigin); } catch { /* hint only */ }
    })();
  }, [isSuperAdmin, getToken]);

  const toggle = async (p: PixelRow) => {
    setBusy(p.id);
    try {
      const token = await getToken();
      if (!token) { return; }
      await setPixelStatus(token, p.id, p.status === 'active' ? 'inactive' : 'active');
      await load();
    } catch (e: any) {
      window.alert(e?.response?.data?.error || e.message);
    } finally { setBusy(null); }
  };

  const totals = useMemo(() => {
    const t = { pings: 0, visitors: 0, matches: 0, delivered: 0, failed: 0 };
    for (const p of pixels) {
      const s = p.stats?.[range];
      if (!s) { continue; }
      t.pings += s.pings; t.visitors += s.unique_visitors; t.matches += s.matches; t.delivered += s.delivered; t.failed += s.delivery_failed;
    }
    return t;
  }, [pixels, range]);

  if (!isSuperAdmin) {
    return <div className="page"><p style={{ color: '#9ca3af' }}>Super admin access required.</p></div>;
  }

  const card: React.CSSProperties = { background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', minWidth: 140, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' };
  const rangeLabel = range === 'today' ? 'Today (UTC)' : range === 'd7' ? '7 days' : '30 days';

  return (
    <div className="page" style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 22, color: '#1f2937' }}>Identity Pixels</h1>
        <Badge color={env === 'test' ? '#f59e0b' : '#22c55e'}>{env === 'test' ? 'STAGING · TEST KEYS' : 'PRODUCTION · LIVE KEYS'}</Badge>
        <div style={{ flex: 1 }} />
        <select style={{ ...input, width: 130 }} value={range} onChange={e => setRange(e.target.value as typeof range)}>
          <option value="today">Today</option><option value="d7">7 days</option><option value="d30">30 days</option>
        </select>
        <button className="btn btn-secondary" onClick={() => void load()}>Refresh</button>
        <button className="btn btn-primary" onClick={() => setEditing('new')}>New pixel</button>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        {[['Pixels', `${pixels.filter(p => p.status === 'active').length} active / ${pixels.length}`], ['Pings', fmt(totals.pings)], ['Visitors', fmt(totals.visitors)],
          ['Matches', fmt(totals.matches)], ['Match rate', totals.visitors ? `${(totals.matches / totals.visitors * 100).toFixed(1)}%` : '—'],
          ['Delivered', fmt(totals.delivered)], ['Delivery failed', fmt(totals.failed)]].map(([k, v]) => (
          <div key={k} style={card}><div style={{ ...label, marginBottom: 6 }}>{k} {k !== 'Pixels' ? `· ${rangeLabel}` : ''}</div><div style={{ fontSize: 20, fontWeight: 700, color: '#1f2937' }}>{v}</div></div>
        ))}
      </div>

      {error && <div style={{ color: '#ef4444', marginBottom: 12 }}>{error}</div>}
      {loading ? <p style={{ color: '#9ca3af' }}>Loading…</p> : (
        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 10, background: '#ffffff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Status', 'Pixel', 'Destination', 'Pings', 'Visitors', 'Matches', 'Det. / IP', 'Match rate', 'Delivered', 'Failed', 'Deduped', 'Bots', 'GPC', 'Dedupe', 'Last ping', ''].map(h =>
                  <th key={h} style={['Pixel', 'Destination', 'Status', ''].includes(h) ? th : { ...th, textAlign: 'right' }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {pixels.map(p => {
                const s = p.stats?.[range];
                const dest = p.destination.type === 'data_body' ? `Body: ${p.destination.dataBodyName ?? '?'}`
                  : p.destination.type === 'custom_api' ? `API: ${(() => { try { return new URL(p.destination.url).host; } catch { return p.destination.url; } })()}` : 'None';
                return (
                  <tr key={p.id}>
                    <td style={td}>
                      <button className="btn btn-secondary" style={{ padding: '2px 10px', fontSize: 12 }} disabled={busy === p.id} onClick={() => void toggle(p)} title={p.status === 'active' ? 'Deactivate' : 'Activate'}>
                        <Badge color={p.status === 'active' ? '#22c55e' : '#6b7280'}>{p.status === 'active' ? 'ACTIVE' : 'OFF'}</Badge>
                      </button>
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{p.allowedDomains.join(', ') || 'no domains'} · {p.formCapture ? 'forms' : 'no forms'} · {p.ipMatching ? 'IP on' : 'IP off'}</div>
                    </td>
                    <td style={{ ...td, fontSize: 12 }}>{dest}</td>
                    <td style={num}>{fmt(s?.pings)}</td>
                    <td style={num}>{fmt(s?.unique_visitors)}</td>
                    <td style={num}>{fmt(s?.matches)}</td>
                    <td style={num}>{fmt(deterministic(s))} / {fmt(s?.matches_ip)}</td>
                    <td style={num}>{s ? `${s.match_rate}%` : '—'}</td>
                    <td style={num}>{fmt(s?.delivered)}</td>
                    <td style={{ ...num, color: s?.delivery_failed ? '#ef4444' : td.color }}>{fmt(s?.delivery_failed)}</td>
                    <td style={num}>{fmt(s?.dup_suppressed)}</td>
                    <td style={num}>{fmt(s?.bots)}</td>
                    <td style={num}>{fmt(s?.gpc_optouts)}</td>
                    <td style={num}>{p.dedupeDays ? `${p.dedupeDays}d` : 'off'}</td>
                    <td style={{ ...num, whiteSpace: 'nowrap' }}>{ago(p.lastPingAt)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: 12, marginRight: 4 }} onClick={() => setReporting(p.id)}>Report</button>
                      <button className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: 12, marginRight: 4 }} onClick={() => setEditing(p)}>Edit</button>
                      <button className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: 12 }}
                        onClick={() => setInstalling({ pixel: p, snippet: snippetFor(p, origin) })}>Install</button>
                    </td>
                  </tr>
                );
              })}
              {!pixels.length && <tr><td style={td} colSpan={16}>No pixels yet. Click New pixel.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <Editor
          pixel={editing === 'new' ? null : editing} env={env} bodies={bodies} coreFields={fields.core} metaFields={fields.meta}
          onClose={() => setEditing(null)}
          onSaved={(snip) => {
            const wasNew = editing === 'new';
            setEditing(null);
            void load();
            if (wasNew && snip) { window.alert('Pixel created (inactive). Open Install for the snippet, then Activate.'); }
          }}
        />
      )}
      {installing && <Install pixel={installing.pixel} snippet={installing.snippet} onClose={() => setInstalling(null)} onRotated={() => void load()} />}
      {reporting && <Report pixelId={reporting} onClose={() => setReporting(null)} />}
    </div>
  );
}

/** Snippet built client-side from the key + the collector origin the key belongs to. */
function snippetFor(p: PixelRow, metaOrigin: string): string {
  const origin = metaOrigin || (p.env === 'live' ? 'https://c.thiccrails.com' : 'https://c-stg.thiccrails.com');
  return [
    '<!-- Identity pixel: paste before </head> -->',
    '<script>',
    "!function(w,d,k){w.trq=w.trq||[];var s=d.createElement('script');s.async=1;",
    `s.src='${origin}/v1/t.js?k='+k;d.head.appendChild(s)}(window,document,'${p.publicKey}');`,
    '</script>',
  ].join('\n');
}
